import { makeVar, useReactiveVar } from '@apollo/client';
import unLinkSurfaceNodes from 'graphql/surface-node/mutations/unlink-surface-nodes';
import linkSurfaceNodes from 'graphql/surface-node/mutations/link-surface-nodes';
import useSaveNodeInCache from 'components/canvas/hooks/use-save-node-in-cache';
import useFindNodeInCache from 'components/canvas/hooks/use-find-node';
import surfaceNode from 'graphql/surface-node/queries/surface-node';
import { toast } from 'services/toast';
import { NODE_TYPE_MAP } from 'services/apollo-client';
import { notEmpty } from 'utils/js';
import { set, compose } from 'lodash/fp';
import { color } from 'styles/settings';
import { LinkSettings, Position } from 'types';
import {
  LineShape,
  LinkType,
  SurfaceNodePointsTo,
  SurfaceNodeUnion,
} from 'graphql/types';

export interface MarkForDeletionInput {
  from: SurfaceNodeUnion;
  to: SurfaceNodeUnion;
}

export interface NodeLinkingState {
  from?: SurfaceNodeUnion | null;
  to?: SurfaceNodeUnion | null;
  settings?: LinkSettings;
  linkMarkedForDeletion?: MarkForDeletionInput | null;
  linkStartPosition?: Position | undefined;
}

interface NodeLinkingFunctions extends NodeLinkingOperations {
  updateLinkSettingsInCache(input: {
    link: SurfaceNodePointsTo;
    settings: LinkSettings;
  }): void;
  addConnection(link: SurfaceNodePointsTo): void;
  removeConnection(input: SurfaceNodePointsTo): void;
}

interface NodeLinkingOperations {
  markLinkForDeletion(input: MarkForDeletionInput): void;
  setLinkArrowShown(fromNodeId: string): void;
  startLinking(from: SurfaceNodeUnion): void;
  removeConnectionFromNode(
    node: SurfaceNodeUnion,
    link: SurfaceNodePointsTo,
  ): SurfaceNodeUnion;
  addConnectionToNode(
    node: SurfaceNodeUnion,
    link: SurfaceNodePointsTo,
  ): SurfaceNodeUnion;
  getLinkMarkedForDeletion(): MarkForDeletionInput | null | undefined;
  getCurrentLink(): NodeLinkingState;
  removeLinkArrow(): void;
  clearLink(): void;
  linkNodes(link: SurfaceNodePointsTo): Promise<any>;
  deleteLink(link: SurfaceNodePointsTo): Promise<any>;
  createLink(input: SurfaceNodeUnion): SurfaceNodeUnion;
  reverseLink(link: SurfaceNodePointsTo): SurfaceNodePointsTo;
  reverseSettings(settings: LinkSettings): LinkSettings;
  isBidirectionalLink(
    link: SurfaceNodePointsTo,
    otherLink: SurfaceNodePointsTo,
  ): boolean;
  areLinksEqual(
    link: SurfaceNodePointsTo,
    otherLink: SurfaceNodePointsTo,
  ): boolean;
  getIsLinkArrowShown(): boolean;
  showLinkArrow(
    from: SurfaceNodeUnion,
    linkStartPosition?: Position,
    linkToDelete?: any,
  ): void;
}

const nodeLinkingVar = makeVar<NodeLinkingState>({});
const linkStartPositionVar = makeVar<Position | undefined>(undefined);
const linkArrowStartsFromVar = makeVar<string | undefined>(undefined);

const defaultSettings = {
  color: 'rgba(0, 0, 0, 1)',
  startShape: LineShape.Line,
  endShape: LineShape.Line,
  linkType: LinkType.Full,
  linkName: '',
};

const nodeLinkingOperations: NodeLinkingOperations = {
  reverseLink,
  reverseSettings,
  isBidirectionalLink,
  areLinksEqual,
  createLink,
  deleteLink,
  getIsLinkArrowShown,
  showLinkArrow,
  removeLinkArrow,
  clearLink,
  linkNodes,
  getCurrentLink,
  getLinkMarkedForDeletion,
  markLinkForDeletion,
  setLinkArrowShown,
  addConnectionToNode,
  startLinking,
  removeConnectionFromNode,
};

export function useNodeLinking(
  surfaceId: string,
): [NodeLinkingState & { isLinkArrowShown: boolean }, NodeLinkingFunctions] {
  const nodeLinking = useReactiveVar(nodeLinkingVar);
  const linkArrowStartsFrom = useReactiveVar(linkArrowStartsFromVar);
  const isLinkArrowShown = Boolean(linkArrowStartsFrom);
  const linkStartPosition = useReactiveVar(linkStartPositionVar);
  const saveNodeInCache = useSaveNodeInCache();

  const { findNodeFromLink } = useFindNodeInCache(surfaceId);

  const addConnection = addConnectionHOF(findNodeFromLink, saveNodeInCache);

  const removeConnection = removeConnectionHOF(
    findNodeFromLink,
    saveNodeInCache,
  );

  function updateLinkSettingsInCache({
    link,
    settings,
  }: {
    link: SurfaceNodePointsTo;
    settings: LinkSettings;
  }) {
    const fromNode = findNodeFromLink(link.from);
    const toNode = findNodeFromLink(link.to);

    function updateSettings({ pointsTo, link, settings }) {
      return pointsTo.map((pointsTo) => {
        if (
          link.from.id === pointsTo.from.id &&
          link.to.id === pointsTo.to.id
        ) {
          return {
            ...pointsTo,
            settings: {
              ...pointsTo?.settings,
              ...settings,
              updatedAt: new Date().toISOString(),
            },
          };
        }

        return pointsTo;
      });
    }

    if (fromNode) {
      const pointsTo = fromNode.pointsTo;
      const newPointsTo = updateSettings({ pointsTo, link, settings });

      saveNodeInCache(set('pointsTo', newPointsTo, fromNode));
    }

    if (toNode) {
      const pointsTo = toNode.pointsTo as any;
      const toLink = reverseLink(link);
      const reverseLinkSettings = reverseSettings(settings);
      const newPointsTo = updateSettings({
        pointsTo,
        link: toLink,
        settings: reverseLinkSettings,
      });

      saveNodeInCache(set('pointsTo', newPointsTo, toNode));
    }
  }

  return [
    { ...nodeLinking, isLinkArrowShown, linkStartPosition },
    {
      ...nodeLinkingOperations,
      addConnection,
      removeConnection,
      updateLinkSettingsInCache,
    },
  ];
}

export function useNodeLinkingOperations(): NodeLinkingOperations & {
  addConnection(link: SurfaceNodePointsTo): void;
  removeConnection(input: SurfaceNodePointsTo): void;
} {
  const saveNodeInCache = useSaveNodeInCache();

  const { findNodeFromLink } = useFindNodeInCache();

  const addConnection = addConnectionHOF(findNodeFromLink, saveNodeInCache);

  const removeConnection = removeConnectionHOF(
    findNodeFromLink,
    saveNodeInCache,
  );

  return { ...nodeLinkingOperations, addConnection, removeConnection };
}

export function useIsLinkArrowShown() {
  const linkArrowStartsFrom = useReactiveVar(linkArrowStartsFromVar);

  return Boolean(linkArrowStartsFrom);
}

export function useIsLinkArrowStartingFromNode(
  node: SurfaceNodeUnion,
): boolean {
  const linkArrowStartsFrom = useReactiveVar(linkArrowStartsFromVar);

  return linkArrowStartsFrom === node.id;
}

export function useDoesLinkStartFromNode(node: SurfaceNodeUnion): boolean {
  const linkArrowStartsFrom = useReactiveVar(linkArrowStartsFromVar);

  return linkArrowStartsFrom === node.id;
}

export function getDoesLinkStartFromNode(node: SurfaceNodeUnion) {
  return linkArrowStartsFromVar() === node.id;
}

function showLinkArrow(
  from: SurfaceNodeUnion,
  linkStartPosition?: Position,
  linkToDelete?: any,
): void {
  setLinkArrowShown(from.id);

  if (linkToDelete) {
    markLinkForDeletion(linkToDelete);
  }

  if (linkStartPosition) {
    linkStartPositionVar(linkStartPosition);
  }

  startLinking(from);
}

function startLinking(from: SurfaceNodeUnion): void {
  updateNodeLinking({ from: createLink(from), to: null });
}

function markLinkForDeletion(linkMarkedForDeletion: MarkForDeletionInput) {
  return updateNodeLinking({ linkMarkedForDeletion });
}

function updateNodeLinking(input: NodeLinkingState) {
  nodeLinkingVar({ ...nodeLinkingVar(), ...input });
}

function setLinkArrowShown(fromNodeId: string | undefined): void {
  linkArrowStartsFromVar(fromNodeId);
}

function getIsLinkArrowShown(): boolean {
  return Boolean(linkArrowStartsFromVar());
}

function getLinkMarkedForDeletion(): MarkForDeletionInput | undefined | null {
  return nodeLinkingVar().linkMarkedForDeletion;
}

function getCurrentLink(): NodeLinkingState {
  return nodeLinkingVar();
}

async function deleteLink(link: SurfaceNodePointsTo): Promise<any> {
  const input = { fromId: link.from.id, toId: link.to.id };

  const response = await unLinkSurfaceNodes({ input });

  if (response.isFailure) {
    toast.error(response.getErrorMessage());
  }

  return response;
}

function clearLink() {
  return updateNodeLinking({
    from: null,
    to: null,
    linkMarkedForDeletion: null,
  });
}

function removeLinkArrow() {
  setLinkArrowShown(undefined);

  if (linkStartPositionVar()) {
    linkStartPositionVar();
  }
  clearLink();
}

export function reverseLink(link: SurfaceNodePointsTo): SurfaceNodePointsTo {
  return {
    ...(link.settings ? { settings: reverseSettings(link?.settings) } : {}),
    from: link.to,
    to: link.from,
  };
}

export function reverseSettings(settings?: LinkSettings): LinkSettings {
  return {
    ...settings,
    ...(settings?.endShape ? { startShape: settings?.endShape } : {}),
    ...(settings?.startShape ? { endShape: settings?.startShape } : {}),
  };
}

export function isBidirectionalLink(
  link: SurfaceNodePointsTo,
  otherLink: SurfaceNodePointsTo,
): boolean {
  return link.from.id === otherLink.to.id && link.to.id === otherLink.from.id;
}

export function areLinksEqual(
  link: SurfaceNodePointsTo,
  otherLink: SurfaceNodePointsTo,
): boolean {
  return link.from.id === otherLink.from.id && link.to.id === otherLink.to.id;
}

async function linkNodes({ from, to }: SurfaceNodePointsTo): Promise<any> {
  const fromInput = {
    id: from.id,
    //position: from?.position,
    surface: from?.surface?.id,
  };

  const toInput = {
    id: to.id,
    // position: to?.position,
    surface: to?.surface?.id,
  };

  const input = {
    from: fromInput,
    to: toInput,
  };

  return linkSurfaceNodes({ input }).then((response) => {
    if (response.isFailure) {
      toast.error(response.getErrorMessage());
    }

    return response;
  });
}

function fetchSurfaceNodeFromServer(id: string) {
  return surfaceNode({ id }).then((response) => {
    if (response.isSuccess) {
      return response.getField('data');
    }

    return null;
  });
}

function addConnectionToNode(
  node: SurfaceNodeUnion,
  link: SurfaceNodePointsTo,
): SurfaceNodeUnion {
  // @ts-ignore
  if (!link.__typename) {
    link = compose(
      set('__typename', 'SurfaceNodePointsTo'),
      set('from.__typename', NODE_TYPE_MAP[link.from?.type]),
      set('to.__typename', NODE_TYPE_MAP[link.to?.type]),
    )(link);
  }

  if (notEmpty(node.pointsTo)) {
    return {
      ...node,
      // @ts-ignore
      pointsTo: [...node.pointsTo, link],
    };
  }

  return {
    ...node,
    // @ts-ignore
    pointsTo: [link],
  };
}

function removeConnectionFromNode(
  node: SurfaceNodeUnion,
  link: SurfaceNodePointsTo,
): SurfaceNodeUnion {
  return {
    ...node,
    // @ts-ignore
    pointsTo: node.pointsTo?.filter(({ to }) => to.id !== link.to.id),
  };
}

function createLink(input: SurfaceNodeUnion): SurfaceNodeUnion {
  const link = input;
  /*pick(input, [
    'id',
    'name',
    'text',
    'url',
    'type',
    'question',
    'answerType',
    'surface.id',
    'surface.name',
    'surface.__typename',
    'settings',
    'containerNode',
    '__typename',
  ]) as Link;*/

  if (!link.surface) {
    // @ts-ignore
    return set('surface', null, link);
  }

  return link;
}

function addConnectionHOF(
  findNodeFromLink,
  saveNodeInCache,
): (pointsTo: SurfaceNodePointsTo) => Promise<void> {
  return async function addConnection(
    pointsTo: SurfaceNodePointsTo,
  ): Promise<void> {
    const from = createLink(pointsTo.from);
    const to = createLink(pointsTo.to);

    const settings = pointsTo.settings ?? defaultSettings;

    let fromNode = findNodeFromLink(from);
    let toNode = findNodeFromLink(to);

    if (!fromNode && !from.surface) {
      fromNode = await fetchSurfaceNodeFromServer(from.id);
    }

    if (!toNode && !to.surface) {
      toNode = await fetchSurfaceNodeFromServer(to.id);
    }

    const alreadyConnected = fromNode?.pointsTo?.some(
      (link) => link?.to?.id === to?.id,
    );

    if (fromNode && toNode && alreadyConnected === false) {
      const updatedAt = new Date().toISOString();
      saveNodeInCache(
        addConnectionToNode(set('updatedAt', updatedAt, fromNode), {
          from,
          to,
          settings,
        }),
      );

      saveNodeInCache(
        addConnectionToNode(
          set('updatedAt', updatedAt, toNode),
          reverseLink({ from, to, settings }),
        ),
      );
    }
  };
}

function removeConnectionHOF(
  findNodeFromLink,
  saveNodeInCache,
): (pointsTo: SurfaceNodePointsTo) => void {
  return function removeConnection(pointsTo: SurfaceNodePointsTo) {
    const from = createLink(pointsTo.from);
    const to = createLink(pointsTo.to);
    const startingNode = findNodeFromLink(from);
    const endingNode = findNodeFromLink(to);

    if (startingNode) {
      const newPointsTo = startingNode?.pointsTo?.filter(
        (link) => to.id !== link?.to.id,
      );

      saveNodeInCache(set('pointsTo', newPointsTo, startingNode));
    }

    if (endingNode) {
      const newPointsTo = endingNode?.pointsTo?.filter(
        (link) => from.id !== link?.to.id,
      );

      saveNodeInCache(set('pointsTo', newPointsTo, endingNode));
    }
  };
}
